(() => {
  // Cache-bust: 2026-10-16 flat-fee cart correction.
  // Easify falls back to reloading the entire product page when it cannot
  // identify the theme's cart drawer integration. We handle its cart events
  // below and refresh the drawer through the theme's native event instead.
  window.easifyAddToCartEvent = true;

  const SINGLE_UNIT_PATTERN = /^(?:1|1\s*[-–—]\s*9)\s+units?\b/i;
  const NO_LABEL_APPLICATION_PREFIX = 'No thanks';
  const LABEL_PRINTING_YES_PATTERN = /^yes,?\s+add label printing/i;
  const LABEL_APPLICATION_YES_PATTERN = /^yes,?\s+add label application/i;
  const LABEL_PACKS = {
    10: { label: 'x10 Label Pack', price: '$15', priceValue: 15, perLabel: '$1.50ea' },
    25: { label: 'x25 Label Pack', price: '$35', priceValue: 35, perLabel: '$1.40ea' },
    50: { label: 'x50 Label Pack', price: '$65', priceValue: 65, perLabel: '$1.30ea' },
    100: { label: 'x100 Label Pack', price: '$120', priceValue: 120, perLabel: '$1.20ea' },
    200: { label: 'x200 Label Pack', price: '$220', priceValue: 220, perLabel: '$1.10ea' },
  };
  const LABEL_PRINTING_UNAVAILABLE_QUANTITIES = new Set([500, 1000]);
  const ONE_TIME_LABEL_PRINTING_VARIANT_IDS = new Set([
    // Label Printing / x10 units ($1.50ea). This is the only label option
    // selected while the retail product itself uses a cart quantity of 1–9.
    '46231069884595',
  ]);
  const LABEL_PRINTING_PRICE_BY_VARIANT_ID = {
    // The visible x10 Label Printing product used only for 1–9 orders.
    '46231069884595': 15,
    // Legacy Label Reprint variants used by Easify's automated fixed packs.
    '44437096464563': 15,
    '44437096497331': 35,
    '44437096530099': 65,
    '44437096562867': 120,
    '44437096595635': 220,
  };
  const LABEL_APPLICATION_PACKS = {
    10: { label: 'Label application for 10 labels', price: '$20', priceValue: 20, perLabel: '$2.00ea' },
    25: { label: 'Label application for 25 labels', price: '$45', priceValue: 45, perLabel: '$1.80ea' },
    50: { label: 'Label application for 50 labels', price: '$80', priceValue: 80, perLabel: '$1.60ea' },
    100: { label: 'Label application for 100 labels', price: '$140', priceValue: 140, perLabel: '$1.40ea' },
    200: { label: 'Label application for 200 labels', price: '$240', priceValue: 240, perLabel: '$1.20ea' },
  };
  const HIDDEN_CLASS = 'retail-no-moq-hidden';
  const SINGLE_CLASS = 'retail-no-moq-single';
  const BULK_CLASS = 'retail-no-moq-bulk';
  const LABEL_NOTICE_CLASS = 'retail-no-moq-label-application-notice';
  const LABEL_PACK_AUTOMATED_CLASS = 'retail-no-moq-label-pack-automated';
  const LABEL_APPLICATION_AUTOMATED_CLASS = 'retail-no-moq-label-application-automated';
  const LABEL_PACK_TOTAL_CLASS = 'retail-no-moq-label-pack-total';
  const LARGE_ORDER_LABEL_NOTICE_CLASS = 'retail-no-moq-large-order-label-notice';
  const DEFERRED_VALIDATION_CLASS = 'retail-no-moq-validation-deferred';
  const VISIBLE_LABEL_PRINTING_LINK_SELECTOR = 'a[href*="/products/label-printing"]';
  const EASIFY_SYNC_DELAYS = [300, 1000, 2500];
  let easifySyncTimers = [];
  let isSyncingLabelApplication = false;
  let isResettingUnavailableLabels = false;
  // Easify adds its linked product in a follow-up request. By then it may have
  // already refreshed parts of the product form, so keep the selected retail
  // pack quantity independently instead of relying only on the live dropdown.
  let activeRetailPackQuantity = null;
  let oneTimeLabelPrintingAccepted = false;
  let oneTimeLabelPrintingWindowEndsAt = 0;
  let visibleLabelPrintingObserver = null;

  const isSalonProduct = () => Boolean(getSalonLabelOption());

  const getSalonLabelOption = () =>
    Array.from(
      document.querySelectorAll('product-info .tpo_option-container')
    ).find((option) => {
      const label = option
        .querySelector('.tpo_option-label-container')
        ?.textContent
        ?.replace(/\s+/g, ' ')
        .trim()
        .toLowerCase() || '';

      return label.includes('choose your label') &&
        label.includes('professional skincare') &&
        label.includes('upload your own');
    });

  const getSalonLabelSelection = () => {
    const option = getSalonLabelOption();
    const checked = option?.querySelector('input[type="checkbox"]:checked, input[type="radio"]:checked');
    const value = checked?.value?.replace(/\s+/g, ' ').trim() || '';

    if (/professional skincare label/i.test(value)) return 'Professional Skincare Label';
    if (/upload your own label/i.test(value)) return 'Upload Your Own Label';
    return '';
  };

  const normaliseSalonLineItemProperties = (item) => {
    if (!isSalonProduct()) return false;

    const properties = item?.properties;
    if (!properties || !Object.hasOwn(properties, 'Unit Qty')) return false;

    const labelType = getSalonLabelSelection() ||
      (/professional skincare label/i.test(properties['Label Printing'] || '')
        ? 'Professional Skincare Label'
        : (/upload your own label/i.test(properties['Label Printing'] || '')
          ? 'Upload Your Own Label'
          : ''));

    if (!labelType) return false;

    item.properties = {
      ...properties,
      'Label Printing': 'Yes',
      'Label Type': labelType,
      'Label Application': 'Included – salon labels applied free of charge',
    };

    return true;
  };

  const removeLegacyTpoProperties = (item) => {
    const properties = item?.properties;
    if (!properties || typeof properties !== 'object') return false;

    let changed = false;
    const cleanProperties = {};

    Object.entries(properties).forEach(([name, value]) => {
      if (/^_?tpo_/i.test(name)) {
        changed = true;
        return;
      }

      cleanProperties[name] = value;
    });

    if (changed) item.properties = cleanProperties;
    return changed;
  };

  const parseJsonArray = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value !== 'string' || !value) return [];

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  };

  const parseRetailPackQuantity = (value = '') => {
    if (SINGLE_UNIT_PATTERN.test(value.trim())) return 1;

    const quantityMatch = value.match(/(?:^|[^\d])(1000|500|200|100|50|25|10)(?=[^\d]|$)/);
    return quantityMatch ? Number(quantityMatch[1]) : null;
  };

  const getSelectedRetailPackQuantityFromDom = () => {
    const productInfo = document.querySelector('product-info');
    const quantityWrapper = Array.from(
      productInfo?.querySelectorAll('[data-option-position]') || []
    ).find((wrapper) => {
      const name = wrapper
        .querySelector('.radio__legend__option-name')
        ?.textContent
        ?.trim()
        .toLowerCase();
      return name?.includes('choose your qty');
    });
    const value = quantityWrapper
      ?.querySelector('[data-popout-toggle-text]')
      ?.textContent
      ?.trim() || '';

    return parseRetailPackQuantity(value) ?? activeRetailPackQuantity;
  };

  const installOneTimeLabelPrintingGuard = () => {
    if (window.__retailNoMoqOneTimeLabelPrintingGuard || typeof window.fetch !== 'function') {
      return;
    }

    window.__retailNoMoqOneTimeLabelPrintingGuard = true;
    const originalFetch = window.fetch;

    window.fetch = function retailNoMoqFetch(input, init) {
      try {
        const url = typeof input === 'string' ? input : input?.url;
        const pathname = new URL(url, window.location.origin).pathname;
        const isCartAdd = pathname === '/cart/add' || pathname === '/cart/add.js';

        if (isCartAdd && typeof init?.body === 'string') {
          const body = JSON.parse(init.body);

          if (Array.isArray(body?.items)) {
            let changed = false;
            const items = body.items.flatMap((item) => {
              const nextItem = { ...item };
              if (normaliseSalonLineItemProperties(nextItem)) changed = true;
              const itemId = String(nextItem.id || nextItem.variant_id || '');
              const selectedProductQuantity = getSelectedRetailPackQuantityFromDom();
              const isSingleUnitOrder = selectedProductQuantity === 1;

              // Fixed packs (x10 and above) must remain on Easify's native
              // cart workflow. Shopify rejects attempts to replace the only
              // linked add-on with a synthetic price property, so leave every
              // fixed-pack item and its linked charge untouched.
              if (!isSingleUnitOrder || isSalonProduct()) return [nextItem];

              // Easify can submit the linked product directly. It must always
              // remain a single label pack, even when the main product quantity
              // is between 2 and 9.
              if (Object.hasOwn(LABEL_PRINTING_PRICE_BY_VARIANT_ID, itemId)) {
                // Only the dedicated Label Printing x10 product is allowed to
                // remain visible for a 1–9 order.
                if (!ONE_TIME_LABEL_PRINTING_VARIANT_IDS.has(itemId)) {
                  changed = true;
                  return [];
                }

                if (isSingleUnitOrder) {
                  // Easify can emit the linked product more than once during a
                  // single submission (normal add-on plus one-time-charge
                  // passes). Keep only the first request so the order always
                  // contains exactly one $15 label pack.
                  if (Date.now() > oneTimeLabelPrintingWindowEndsAt) {
                    oneTimeLabelPrintingAccepted = false;
                    oneTimeLabelPrintingWindowEndsAt = Date.now() + 10000;
                  }

                  if (oneTimeLabelPrintingAccepted) {
                    changed = true;
                    return [];
                  }

                  oneTimeLabelPrintingAccepted = true;
                }

                if (isSingleUnitOrder && Number(nextItem.quantity) !== 1) {
                  nextItem.quantity = 1;
                }

                changed = true;
              }

              const properties = nextItem.properties;
              if (!properties) return [nextItem];

              let addOnVariantIds = parseJsonArray(properties._tpo_add_on_variant_ids);

              const selectedOneTimeIds = addOnVariantIds.filter((id) =>
                ONE_TIME_LABEL_PRINTING_VARIANT_IDS.has(String(id))
              );

              if (!selectedOneTimeIds.length) return [nextItem];

              const existingOneTimeIds = parseJsonArray(properties._tpo_one_time_charges);
              const mergedOneTimeIds = [...existingOneTimeIds];

              selectedOneTimeIds.forEach((id) => {
                if (!mergedOneTimeIds.some((existingId) => String(existingId) === String(id))) {
                  mergedOneTimeIds.push(id);
                }
              });

              nextItem.properties = {
                ...properties,
                _tpo_one_time_charges: JSON.stringify(mergedOneTimeIds),
              };

              // The $15 is now represented by its own visible Label Printing
              // cart line. Do not also present it as a per-product adjustment
              // on the 1–9 retail line, otherwise Easify displays the fee once
              // for every unit even though Shopify charges the add-on only once.
              const mainQuantity = Math.max(Number(nextItem.quantity) || 1, 1);
              const additionalPrice = Number(nextItem.properties._tpo_price);

              if (mainQuantity > 1 && Number.isFinite(additionalPrice) && additionalPrice > 0) {
                nextItem.properties._tpo_price = 0;
              }

              if (mainQuantity > 1 && nextItem.properties['Label Qty']) {
                nextItem.properties['Label Qty'] = 'x10';
              }

              changed = true;
              return [nextItem];
            });

            // TPO/Qikify is no longer installed. Keep its properties available
            // to the legacy calculations above, then remove them from every
            // final line item before Shopify creates the cart or order.
            items.forEach((item) => {
              if (removeLegacyTpoProperties(item)) changed = true;
            });

            if (changed) {
              if (!items.length) {
                // Easify treats an empty `items` response as a failed add-on
                // request and shows "An error occurred in your cart" when
                // label printing is the only paid service selected. Acknowledge
                // the original linked-product payload so Easify can finish its
                // workflow, while deliberately skipping the real Shopify cart
                // request. The matching fee is already folded into the main
                // retail line's `_tpo_price` above.
                return Promise.resolve(new Response(JSON.stringify({ items: body.items }), {
                  status: 200,
                  headers: { 'Content-Type': 'application/json' },
                }));
              }

              init = {
                ...init,
                body: JSON.stringify({ ...body, items }),
              };
            }
          }
        }
      } catch (error) {
        console.warn('Unable to apply the one-time label printing safeguard.', error);
      }

      return originalFetch.call(this, input, init);
    };
  };

  const revealVisibleLabelPrintingLines = (root = document) => {
    const links = [];

    if (root instanceof Element && root.matches(VISIBLE_LABEL_PRINTING_LINK_SELECTOR)) {
      links.push(root);
    }

    if (typeof root.querySelectorAll === 'function') {
      links.push(...root.querySelectorAll(VISIBLE_LABEL_PRINTING_LINK_SELECTOR));
    }

    links.forEach((link) => {
      const cartItem = link.closest('.cart__item');
      if (!cartItem) return;

      cartItem.hidden = false;
      cartItem.removeAttribute('hidden');
      cartItem.removeAttribute('aria-hidden');
      cartItem.classList.remove('tpo_hidden', 'tpo_add_on_product');

      if (
        cartItem.style.getPropertyValue('display') !== 'flex' ||
        cartItem.style.getPropertyPriority('display') !== 'important'
      ) {
        cartItem.style.setProperty('display', 'flex', 'important');
      }

      cartItem.style.removeProperty('visibility');
      cartItem.dataset.retailLabelPrintingVisible = 'true';
    });
  };

  const keepVisibleLabelPrintingLinesInCart = () => {
    revealVisibleLabelPrintingLines();

    if (visibleLabelPrintingObserver || !document.body) return;

    let revealFrame = null;
    visibleLabelPrintingObserver = new MutationObserver((mutations) => {
      const affectsLabelLine = mutations.some((mutation) => {
        if (mutation.type === 'childList') {
          return mutation.addedNodes.length > 0;
        }

        return mutation.target instanceof Element && (
          mutation.target.matches(VISIBLE_LABEL_PRINTING_LINK_SELECTOR) ||
          mutation.target.closest('.cart__item')?.querySelector(VISIBLE_LABEL_PRINTING_LINK_SELECTOR)
        );
      });

      if (!affectsLabelLine || revealFrame !== null) return;

      revealFrame = window.requestAnimationFrame(() => {
        revealFrame = null;
        revealVisibleLabelPrintingLines();
      });
    });

    visibleLabelPrintingObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden'],
    });
  };

  installOneTimeLabelPrintingGuard();

  const installEasifyCartDrawerBridge = () => {
    if (window.__retailNoMoqEasifyCartDrawerBridge) return;

    window.__retailNoMoqEasifyCartDrawerBridge = true;
    let pendingRefresh = null;

    const refreshCartDrawer = () => {
      window.clearTimeout(pendingRefresh);
      pendingRefresh = null;
      document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
    };

    // If there is no paid add-on, the main-product event is the final event.
    // Give Easify a short window to emit its add-on event before refreshing.
    document.addEventListener('easify:main-product:add', () => {
      window.clearTimeout(pendingRefresh);
      pendingRefresh = window.setTimeout(refreshCartDrawer, 700);
    });

    // Paid label printing/application products are added after the main line.
    // Refresh only after that final request completes so the drawer never
    // opens with a temporary or incomplete cart state.
    document.addEventListener('easify:add-on-product:add', () => {
      window.clearTimeout(pendingRefresh);
      pendingRefresh = window.setTimeout(refreshCartDrawer, 50);
    });
  };

  installEasifyCartDrawerBridge();

  const installCartDisplayPriceGuard = () => {
    if (window.__retailNoMoqCartDisplayPriceGuard) return;

    window.__retailNoMoqCartDisplayPriceGuard = true;
    let animationFrame = null;

    const enforceDisplayPrices = () => {
      animationFrame = null;

      document
        .querySelectorAll('#cart-drawer .cart__item[data-retail-display-total]:not([hidden])')
        .forEach((cartItem) => {
          const displayTotal = cartItem.dataset.retailDisplayTotal;
          if (!displayTotal) return;

          cartItem.querySelectorAll('.cart__item__price, .cart__price').forEach((price) => {
            if (price.textContent.trim() !== displayTotal) {
              price.textContent = displayTotal;
            }
          });
        });
    };

    const scheduleDisplayPriceCheck = () => {
      if (animationFrame !== null) return;
      animationFrame = window.requestAnimationFrame(enforceDisplayPrices);
    };

    const observeCartDrawer = () => {
      const cartDrawer = document.querySelector('#cart-drawer');
      if (!cartDrawer) return;

      new MutationObserver(scheduleDisplayPriceCheck).observe(cartDrawer, {
        childList: true,
        characterData: true,
        subtree: true,
      });

      scheduleDisplayPriceCheck();
    };

    document.addEventListener('theme:cart:refresh', scheduleDisplayPriceCheck);
    document.addEventListener('easify:main-product:add', scheduleDisplayPriceCheck);
    document.addEventListener('easify:add-on-product:add', scheduleDisplayPriceCheck);

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', observeCartDrawer, { once: true });
    } else {
      observeCartDrawer();
    }
  };

  installCartDisplayPriceGuard();

  const getProductInfo = () => document.querySelector('product-info');

  // Salon products use their own required label choice. Do not run the retail
  // label-printing automation on those products because both option sets reuse
  // the "Label Printing" property name and the generic fallback can overwrite
  // the salon choice with "No".
  const isLabelAutomationEnabled = () => !isSalonProduct();

  const getSelectedVariant = (productInfo = getProductInfo()) => {
    const selectedVariant = productInfo?.querySelector('variant-selects [data-selected-variant]');

    if (!selectedVariant?.textContent) return null;

    try {
      return JSON.parse(selectedVariant.textContent);
    } catch (error) {
      console.warn('Unable to read the selected retail product variant.', error);
      return null;
    }
  };

  const findOptionWrapper = (productInfo, optionName) =>
    Array.from(productInfo?.querySelectorAll('[data-option-position]') || []).find((wrapper) => {
      const name = wrapper.querySelector('.radio__legend__option-name')?.textContent?.trim().toLowerCase();
      return name?.includes(optionName.toLowerCase());
    });

  const getSelectedOptionValue = (wrapper, variant = null) => {
    const optionIndex = Number(wrapper?.dataset.optionPosition) - 1;

    return variant?.options?.[optionIndex] ||
      wrapper?.querySelector('[data-popout-toggle-text]')?.textContent?.trim() ||
      '';
  };

  const isSingleUnitSelected = (productInfo, variant = null) => {
    const quantityWrapper = findOptionWrapper(productInfo, 'choose your qty');
    return SINGLE_UNIT_PATTERN.test(getSelectedOptionValue(quantityWrapper, variant).trim());
  };

  const getSelectedProductQuantity = (productInfo, variant = null) => {
    const quantityWrapper = findOptionWrapper(productInfo, 'choose your qty');
    const value = getSelectedOptionValue(quantityWrapper, variant).trim();

    if (SINGLE_UNIT_PATTERN.test(value)) return 1;

    const quantityMatch = value.match(/(?:^|[^\d])(1000|500|200|100|50|25|10)(?=[^\d]|$)/);
    return quantityMatch ? Number(quantityMatch[1]) : null;
  };

  const getLabelPackForQuantity = (quantity) => {
    if (quantity === 1) return { quantity: 10, ...LABEL_PACKS[10] };
    if (!LABEL_PACKS[quantity]) return null;
    return { quantity, ...LABEL_PACKS[quantity] };
  };

  const getLabelApplicationPackForQuantity = (quantity) => {
    if (!LABEL_APPLICATION_PACKS[quantity]) return null;
    return { quantity, ...LABEL_APPLICATION_PACKS[quantity] };
  };

  const findEasifyBlock = (productInfo) =>
    productInfo?.querySelector('[id*="__easify_options_"]') ||
    productInfo?.querySelector('.easify-product-options')?.closest('.shopify-app-block');

  const setQuantity = (productInfo, requestedValue, maximum = null) => {
    const quantityForm = productInfo?.querySelector('.product-form__quantity');
    const quantityInput = quantityForm?.querySelector('[data-quantity-input]');

    if (!quantityForm || !quantityInput) return;

    const value = maximum === null
      ? requestedValue
      : Math.min(Math.max(requestedValue, 1), maximum);

    if (maximum === null) {
      quantityInput.removeAttribute('max');
      quantityInput.removeAttribute('data-max');
    } else {
      quantityInput.max = String(maximum);
      quantityInput.dataset.max = String(maximum);
    }

    quantityInput.min = '1';
    quantityInput.dataset.min = '1';
    quantityInput.step = '1';

    const changed = quantityInput.value !== String(value);
    quantityInput.value = String(value);

    const quantitySelect = quantityForm.querySelector('.select-popout--quantity');
    const toggleText = quantitySelect?.querySelector('[data-popout-toggle-text]');
    const options = quantitySelect?.querySelectorAll('[data-popout-option]') || [];

    if (toggleText) toggleText.textContent = String(value);

    options.forEach((option) => {
      const optionValue = Number(option.dataset.value);
      const optionItem = option.closest('.select-popout__item');
      const isCurrent = optionValue === value;

      optionItem?.classList.toggle('is-active', isCurrent);
      option.toggleAttribute('aria-current', isCurrent);

      if (optionValue > 9) {
        optionItem?.classList.toggle(HIDDEN_CLASS, maximum === 9);
      }
    });

    if (changed) {
      quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const restoreEasify = (easifyBlock) => {
    if (!easifyBlock) return;

    easifyBlock.classList.remove(HIDDEN_CLASS);
    easifyBlock.removeAttribute('aria-hidden');
    easifyBlock.removeAttribute('inert');

    easifyBlock.querySelectorAll('[data-retail-no-moq-disabled]').forEach((control) => {
      control.disabled = control.dataset.retailNoMoqDisabled === 'true';
      delete control.dataset.retailNoMoqDisabled;
    });

    window.dispatchEvent(new Event('resize'));
  };

  const hideEasify = (easifyBlock) => {
    if (!easifyBlock) return;

    easifyBlock.classList.add(HIDDEN_CLASS);
    easifyBlock.setAttribute('aria-hidden', 'true');
    easifyBlock.setAttribute('inert', '');
  };

  const findEasifyOption = (easifyBlock, labelText) =>
    Array.from(easifyBlock?.querySelectorAll('.tpo_option-container') || []).find((container) => {
      const label = container.querySelector('.tpo_option-label-container')?.textContent?.trim().toLowerCase();
      return label?.includes(labelText.toLowerCase());
    });

  const getLabelPrintingValue = (easifyBlock) => {
    const labelPrintingOption = findEasifyOption(easifyBlock, 'label printing');
    return labelPrintingOption
      ?.querySelector('input[name="properties[Label Printing]"]')
      ?.value
      ?.trim() || '';
  };

  const getLabelPrintingInputs = (easifyBlock) => {
    const labelPrintingOption = findEasifyOption(easifyBlock, 'label printing');
    const inputs = Array.from(labelPrintingOption?.querySelectorAll('input[type="checkbox"]') || []);

    return {
      option: labelPrintingOption,
      yes: inputs.find((input) => LABEL_PRINTING_YES_PATTERN.test(input.value)),
      no: inputs.find((input) => !LABEL_PRINTING_YES_PATTERN.test(input.value)),
    };
  };

  const getLabelApplicationInputs = (easifyBlock) => {
    const labelApplicationOption = findEasifyOption(easifyBlock, 'apply your labels');
    const inputs = Array.from(labelApplicationOption?.querySelectorAll('input[type="checkbox"]') || []);

    return {
      option: labelApplicationOption,
      yes: inputs.find((input) => LABEL_APPLICATION_YES_PATTERN.test(input.value)),
      no: inputs.find((input) => !LABEL_APPLICATION_YES_PATTERN.test(input.value)),
    };
  };

  const isLabelPrintingSelected = (easifyBlock) => {
    const { yes, no } = getLabelPrintingInputs(easifyBlock);
    return Boolean(yes?.checked && !no?.checked);
  };

  const isLabelApplicationSelected = (easifyBlock) => {
    const { yes, no } = getLabelApplicationInputs(easifyBlock);
    return Boolean(yes?.checked && !no?.checked);
  };

  const isLabelApplicationAnswered = (easifyBlock) => {
    const { yes, no } = getLabelApplicationInputs(easifyBlock);
    return Boolean(yes?.checked || no?.checked);
  };

  const enforceExclusiveLabelPrintingChoice = (target, easifyBlock) => {
    const { yes, no } = getLabelPrintingInputs(easifyBlock);
    if (target !== yes && target !== no) return;
    if (!target.checked) return;

    const otherInput = target === yes ? no : yes;
    if (otherInput?.checked) otherInput.click();
  };

  const enforceExclusiveLabelApplicationChoice = (target, easifyBlock) => {
    const { yes, no } = getLabelApplicationInputs(easifyBlock);
    if (target !== yes && target !== no) return;
    if (!target.checked) return;

    const otherInput = target === yes ? no : yes;
    if (otherInput?.checked) otherInput.click();
  };

  const syncLabelPrintingAvailability = (easifyBlock, productQuantity) => {
    const { option: labelPrintingOption, yes: yesInput, no: noInput } =
      getLabelPrintingInputs(easifyBlock);
    if (!labelPrintingOption) return;

    const unavailable = LABEL_PRINTING_UNAVAILABLE_QUANTITIES.has(productQuantity);
    labelPrintingOption.classList.toggle(HIDDEN_CLASS, unavailable);
    labelPrintingOption.toggleAttribute('aria-hidden', unavailable);

    if (yesInput) {
      yesInput.disabled = unavailable;
    }

    if (unavailable && LABEL_PRINTING_YES_PATTERN.test(getLabelPrintingValue(easifyBlock))) {
      noInput?.click();
    }
  };

  const syncLabelApplicationAvailability = (easifyBlock, productQuantity) => {
    const { option, yes, no } = getLabelApplicationInputs(easifyBlock);
    if (!option) return;

    const printingSelected = isLabelPrintingSelected(easifyBlock);
    const quantityEligible = Boolean(getLabelApplicationPackForQuantity(productQuantity));
    const shouldShow = printingSelected && quantityEligible;
    const wasShown = option.dataset.retailNoMoqApplicationShown === 'true';

    option.classList.toggle(HIDDEN_CLASS, !shouldShow);
    option.toggleAttribute('aria-hidden', !shouldShow);
    option.dataset.retailNoMoqApplicationShown = String(shouldShow);

    if (yes) yes.disabled = !shouldShow;

    if (!shouldShow) {
      option.classList.remove(DEFERRED_VALIDATION_CLASS);
      delete option.dataset.retailNoMoqValidationReady;
      if (no && !no.checked) {
        isSyncingLabelApplication = true;
        no.click();
        isSyncingLabelApplication = false;
      }
      return;
    }

    // The hidden state uses "No" to keep Easify's required option valid. Clear
    // that automatic answer when the question first becomes visible so the
    // customer must make an intentional choice before label-file fields appear.
    if (!wasShown) {
      option.dataset.retailNoMoqValidationReady = 'false';
      isSyncingLabelApplication = true;
      if (yes?.checked) yes.click();
      if (no?.checked) no.click();
      isSyncingLabelApplication = false;
    }

    const validationReady = option.dataset.retailNoMoqValidationReady === 'true';
    option.classList.toggle(
      DEFERRED_VALIDATION_CLASS,
      !isLabelApplicationAnswered(easifyBlock) && !validationReady
    );
  };

  const syncLabelFileVisibility = (easifyBlock, productQuantity) => {
    const labelsOnFileOption = findEasifyOption(easifyBlock, 'labels on file');
    const uploadedLabelOption = findEasifyOption(easifyBlock, 'upload your label file');
    if (!labelsOnFileOption) return;

    const printingSelected = isLabelPrintingSelected(easifyBlock);
    const applicationRequired = Boolean(getLabelApplicationPackForQuantity(productQuantity));
    const applicationAnswered = isLabelApplicationAnswered(easifyBlock);
    const shouldShow = printingSelected && (!applicationRequired || applicationAnswered);

    labelsOnFileOption.classList.toggle(HIDDEN_CLASS, !shouldShow);
    labelsOnFileOption.toggleAttribute('aria-hidden', !shouldShow);

    if (uploadedLabelOption) {
      uploadedLabelOption.classList.toggle(HIDDEN_CLASS, !shouldShow);
      uploadedLabelOption.toggleAttribute('aria-hidden', !shouldShow);
    }

    if (!shouldShow) {
      labelsOnFileOption
        .querySelectorAll('input[type="checkbox"]:checked')
        .forEach((input) => input.click());
    }
  };

  const syncLabelPackSelection = (easifyBlock, productQuantity) => {
    const labelQuantityOption = findEasifyOption(easifyBlock, 'how many labels');
    if (!labelQuantityOption) return;

    const labelQuantityInput = labelQuantityOption.querySelector('input[name="properties[Label Qty]"]');
    const labelChoices = Array.from(
      labelQuantityOption.querySelectorAll('button.tpo_option-type-selection:not(.tpo_option-type-selection--empty)')
    );
    const noPrintingChoice = labelChoices.find((choice) =>
      /^no label printing\b/i.test(choice.textContent.replace(/\s+/g, ' ').trim())
    );
    const labelPack = getLabelPackForQuantity(productQuantity);
    const labelPrintingSelected = isLabelPrintingSelected(easifyBlock);

    labelQuantityOption.classList.add(LABEL_PACK_AUTOMATED_CLASS);

    labelQuantityOption.querySelector('.retail-no-moq-label-pack-summary')?.remove();

    if (!labelPack || !labelPrintingSelected) {
      if (
        noPrintingChoice &&
        !/^no label printing\b/i.test(labelQuantityInput?.value?.trim() || '')
      ) {
        noPrintingChoice.click();
      }
      return;
    }

    // Easify renders the linked Label Reprint variants as "x10 units ...",
    // while older option values used "10 Label Pack ...". Support both so the
    // automated hidden selection always reaches the cart.
    const labelPackPattern = new RegExp(
      `^x?\\s*${labelPack.quantity}\\s+(?:units?|labels?|label pack)\\b`,
      'i'
    );
    const matchingChoice = labelChoices.find((choice) =>
      labelPackPattern.test(choice.textContent.replace(/\s+/g, ' ').trim())
    );

    if (
      matchingChoice &&
      !labelPackPattern.test(labelQuantityInput?.value?.trim() || '')
    ) {
      matchingChoice.click();
    }
  };

  const syncLabelApplicationSelection = (easifyBlock, productQuantity) => {
    const applicationQuantityOption = findEasifyOption(easifyBlock, 'label application pricing');
    if (!applicationQuantityOption) return;

    const applicationQuantityInput = applicationQuantityOption.querySelector(
      'input[name="properties[Label Application Qty]"]'
    );
    const applicationChoices = Array.from(
      applicationQuantityOption.querySelectorAll(
        'button.tpo_option-type-selection:not(.tpo_option-type-selection--empty)'
      )
    );
    const noApplicationChoice = applicationChoices.find((choice) =>
      /^no label application\b/i.test(choice.textContent.replace(/\s+/g, ' ').trim())
    );
    const applicationPack = getLabelApplicationPackForQuantity(productQuantity);
    const applicationSelected = isLabelApplicationSelected(easifyBlock);

    applicationQuantityOption.classList.add(LABEL_APPLICATION_AUTOMATED_CLASS);

    if (!applicationPack || !applicationSelected) {
      if (
        noApplicationChoice &&
        !/^no label application\b/i.test(applicationQuantityInput?.value?.trim() || '')
      ) {
        noApplicationChoice.click();
      }
      return;
    }

    const applicationPattern = new RegExp(
      `^label application for\\s+${applicationPack.quantity}\\s+labels?\\b`,
      'i'
    );
    const matchingChoice = applicationChoices.find((choice) =>
      applicationPattern.test(choice.textContent.replace(/\s+/g, ' ').trim())
    );

    if (
      matchingChoice &&
      !applicationPattern.test(applicationQuantityInput?.value?.trim() || '')
    ) {
      matchingChoice.click();
    }
  };

  const resetUnavailableLabelOptions = (easifyBlock, productQuantity) => {
    if (!easifyBlock) return;

    const { yes: printingYes, no: printingNo } = getLabelPrintingInputs(easifyBlock);
    const {
      option: applicationOption,
      yes: applicationYes,
      no: applicationNo,
    } = getLabelApplicationInputs(easifyBlock);
    const labelsOnFileOption = findEasifyOption(easifyBlock, 'labels on file');
    const uploadedLabelOption = findEasifyOption(easifyBlock, 'upload your label file');

    isResettingUnavailableLabels = true;

    try {
      // Clear any previous paid selections first, then select Easify's hidden
      // zero-cost fallbacks so its required fields remain valid at checkout.
      if (printingYes?.checked) printingYes.click();
      if (printingNo && !printingNo.checked) printingNo.click();
      if (applicationYes?.checked) applicationYes.click();
      if (applicationNo && !applicationNo.checked) applicationNo.click();

      [labelsOnFileOption, uploadedLabelOption].forEach((option) => {
        option
          ?.querySelectorAll('input[type="checkbox"]:checked')
          .forEach((input) => input.click());

        option
          ?.querySelectorAll('input[type="file"]')
          .forEach((input) => {
            input.value = '';
          });
      });

      if (applicationOption) {
        applicationOption.classList.remove(DEFERRED_VALIDATION_CLASS);
        applicationOption.dataset.retailNoMoqApplicationShown = 'false';
        delete applicationOption.dataset.retailNoMoqValidationReady;
      }

      syncLabelPackSelection(easifyBlock, productQuantity);
      syncLabelApplicationSelection(easifyBlock, productQuantity);
    } finally {
      isResettingUnavailableLabels = false;
    }

    renderAdditionalServicesSummary(easifyBlock, productQuantity);
    hideEasify(easifyBlock);
  };

  const syncLabelAutomation = (easifyBlock, productQuantity) => {
    if (!easifyBlock) return;

    if (LABEL_PRINTING_UNAVAILABLE_QUANTITIES.has(productQuantity)) {
      resetUnavailableLabelOptions(easifyBlock, productQuantity);
      return;
    }

    restoreEasify(easifyBlock);
    syncLabelPrintingAvailability(easifyBlock, productQuantity);
    syncLabelApplicationAvailability(easifyBlock, productQuantity);
    syncLabelFileVisibility(easifyBlock, productQuantity);
    syncLabelPackSelection(easifyBlock, productQuantity);
    syncLabelApplicationSelection(easifyBlock, productQuantity);
    renderAdditionalServicesSummary(easifyBlock, productQuantity);
  };

  const renderAdditionalServicesSummary = (easifyBlock, productQuantity) => {
    const total = easifyBlock?.querySelector('.tpo_total-additional-price-wrapper');
    if (!total) return;

    const labelPack = isLabelPrintingSelected(easifyBlock)
      ? getLabelPackForQuantity(productQuantity)
      : null;
    const applicationPack = isLabelApplicationSelected(easifyBlock)
      ? getLabelApplicationPackForQuantity(productQuantity)
      : null;
    const productInfo = getProductInfo();
    const selectedVariant = getSelectedVariant(productInfo);
    const selectedProductUnits = Number(
      productInfo?.querySelector('[data-quantity-input]')?.value || 1
    );
    const productTotal = selectedVariant
      ? (selectedVariant.price / 100) * selectedProductUnits
      : 0;
    const additionalOptionsSubtotal =
      (labelPack?.priceValue || 0) + (applicationPack?.priceValue || 0);

    const formatDollarAmount = (amount) =>
      `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;

    const addToCartButton = productInfo?.querySelector('[data-add-to-cart]');
    const addToCartText = addToCartButton?.querySelector('[data-add-to-cart-text]');
    const addToCartPrice = addToCartButton?.querySelector('[data-product-price]');

    const syncAddToCartButton = (combinedTotal = null) => {
      if (!addToCartText || !addToCartPrice) return;

      if (combinedTotal === null) {
        addToCartText.textContent = window.theme?.strings?.addToCart || 'Add to cart';
        productInfo?.updateButtonPrice?.();
        return;
      }

      addToCartText.textContent = 'Add to Cart Total';
      addToCartPrice.replaceChildren(
        Object.assign(document.createElement('span'), {
          className: 'btn__price-current',
          textContent: formatDollarAmount(combinedTotal),
        })
      );
    };

    if (!labelPack && !applicationPack) {
      total.classList.remove(LABEL_PACK_TOTAL_CLASS);
      syncAddToCartButton();
      return;
    }

    const summary = document.createElement('div');
    summary.className = 'retail-no-moq-services-summary';

    if (labelPack) {
      const row = document.createElement('p');
      row.replaceChildren(
        document.createTextNode(`Label Printing: ${labelPack.label} `),
        Object.assign(document.createElement('span'), {
          className: 'retail-no-moq-label-pack-price',
          textContent: labelPack.price,
        }),
        document.createTextNode(` (${labelPack.perLabel})`)
      );
      summary.appendChild(row);
    }

    if (applicationPack) {
      const row = document.createElement('p');
      row.replaceChildren(
        document.createTextNode(`Label Application: x${applicationPack.quantity} Labels `),
        Object.assign(document.createElement('span'), {
          className: 'retail-no-moq-label-pack-price',
          textContent: applicationPack.price,
        }),
        document.createTextNode(` (${applicationPack.perLabel})`)
      );
      summary.appendChild(row);
    }

    const optionsSubtotal = document.createElement('p');
    optionsSubtotal.className = 'retail-no-moq-services-total';
    optionsSubtotal.replaceChildren(
      document.createTextNode('Additional options subtotal: '),
      Object.assign(document.createElement('span'), {
        className: 'retail-no-moq-label-pack-price',
        textContent: formatDollarAmount(additionalOptionsSubtotal),
      })
    );
    summary.appendChild(optionsSubtotal);

    total.classList.add(LABEL_PACK_TOTAL_CLASS);
    total.classList.remove('tpo_hidden');
    total.hidden = false;
    total.replaceChildren(summary);
    syncAddToCartButton(productTotal + additionalOptionsSubtotal);
  };

  const syncLabelApplicationNotice = (productInfo, isSingleUnit) => {
    const quantityWrapper = findOptionWrapper(productInfo, 'choose your qty');
    if (!quantityWrapper) return;

    let notice = productInfo.querySelector(`.${LABEL_NOTICE_CLASS}`);

    if (!notice) {
      notice = document.createElement('p');
      notice.className = LABEL_NOTICE_CLASS;
      notice.setAttribute('role', 'status');
      notice.textContent = '** Label application is not available on orders of 9 units or less.';
      quantityWrapper.insertAdjacentElement('afterend', notice);
    }

    notice.hidden = !isSingleUnit;
  };

  const syncLargeOrderLabelNotice = (productInfo, productQuantity) => {
    const quantityWrapper = findOptionWrapper(productInfo, 'choose your qty');
    if (!quantityWrapper) return;

    let notice = productInfo.querySelector(`.${LARGE_ORDER_LABEL_NOTICE_CLASS}`);

    if (!notice) {
      notice = document.createElement('p');
      notice.className = LARGE_ORDER_LABEL_NOTICE_CLASS;
      notice.setAttribute('role', 'status');
      notice.textContent = '** For orders of 500 units or more, labels will need to be sourced separately.';
      quantityWrapper.insertAdjacentElement('afterend', notice);
    }

    notice.hidden = !LABEL_PRINTING_UNAVAILABLE_QUANTITIES.has(productQuantity);
  };

  const scheduleEasifySync = (productInfo) => {
    easifySyncTimers.forEach((timer) => window.clearTimeout(timer));
    easifySyncTimers = EASIFY_SYNC_DELAYS.map((delay) =>
      window.setTimeout(() => {
        const variant = getSelectedVariant(productInfo);
        const easifyBlock = findEasifyBlock(productInfo);
        const productQuantity = getSelectedProductQuantity(productInfo, variant);
        const isSingleUnit = isSingleUnitSelected(productInfo, variant);

        syncLabelApplicationNotice(productInfo, isSingleUnit);
        syncLargeOrderLabelNotice(productInfo, productQuantity);
        if (isLabelAutomationEnabled()) {
          syncLabelAutomation(easifyBlock, productQuantity);
        } else {
          restoreEasify(easifyBlock);
        }
      }, delay)
    );
  };

  const observeEasifyLoad = (productInfo) => {
    if (!productInfo || productInfo.hasAttribute('data-retail-no-moq-easify-observed')) return;

    productInfo.dataset.retailNoMoqEasifyObserved = 'true';

    const observer = new MutationObserver(() => {
      const easifyBlock = findEasifyBlock(productInfo);
      const applicationOption = getLabelApplicationInputs(easifyBlock).option;

      if (!easifyBlock || (isLabelAutomationEnabled() && !applicationOption)) return;

      observer.disconnect();
      applyRetailNoMoqRules();
      scheduleEasifySync(productInfo);
    });

    observer.observe(productInfo, { childList: true, subtree: true });
    window.setTimeout(() => observer.disconnect(), 20000);
  };

  const selectNoLabelApplication = (productInfo, variant) => {
    const labelWrapper = findOptionWrapper(productInfo, 'apply your labels');
    const selectedLabelApplication = getSelectedOptionValue(labelWrapper, variant);

    if (!selectedLabelApplication || selectedLabelApplication.startsWith(NO_LABEL_APPLICATION_PREFIX)) {
      return false;
    }

    const noThanksOption = Array.from(labelWrapper?.querySelectorAll('[data-popout-option]') || [])
      .find((option) => option.dataset.value?.startsWith(NO_LABEL_APPLICATION_PREFIX));

    if (!noThanksOption) return false;

    noThanksOption.click();
    return true;
  };

  const applyRetailNoMoqRules = (variantFromEvent = null) => {
    const productInfo = getProductInfo();
    if (!productInfo) return;

    const variant = variantFromEvent || getSelectedVariant(productInfo);
    if (!variant?.options) return;

    const isSingleUnit = isSingleUnitSelected(productInfo, variant);
    const productQuantity = getSelectedProductQuantity(productInfo, variant);
    if (productQuantity !== null) activeRetailPackQuantity = productQuantity;
    const labelApplicationUnavailable = isSingleUnit ||
      (isLabelAutomationEnabled() && LABEL_PRINTING_UNAVAILABLE_QUANTITIES.has(productQuantity));
    const labelApplicationWrapper = findOptionWrapper(productInfo, 'apply your labels');
    const hideLegacyLabelApplication = isLabelAutomationEnabled() || labelApplicationUnavailable;
    const easifyBlock = findEasifyBlock(productInfo);
    const quantityInput = productInfo.querySelector('[data-quantity-input]');
    const currentQuantity = Number(quantityInput?.value || 1);

    productInfo.classList.toggle(SINGLE_CLASS, isSingleUnit);
    productInfo.classList.toggle(BULK_CLASS, !isSingleUnit);
    // Label application is now priced and collected by Easify. Some older retail
    // products still retain the former Shopify label-application variant option;
    // keep that option on its zero-cost "No thanks" value and hide it so it
    // cannot select an unavailable or double-priced legacy variant.
    labelApplicationWrapper?.classList.toggle(HIDDEN_CLASS, hideLegacyLabelApplication);
    labelApplicationWrapper?.toggleAttribute('aria-hidden', hideLegacyLabelApplication);
    syncLabelApplicationNotice(productInfo, isSingleUnit);
    syncLargeOrderLabelNotice(productInfo, productQuantity);
    if (isLabelAutomationEnabled()) {
      syncLabelAutomation(easifyBlock, productQuantity);
    } else {
      restoreEasify(easifyBlock);
    }

    if (isSingleUnit) {
      setQuantity(productInfo, currentQuantity, 9);
    } else {
      setQuantity(productInfo, 1);
    }

    if (hideLegacyLabelApplication && selectNoLabelApplication(productInfo, variant)) return;
  };

  const initialise = () => {
    keepVisibleLabelPrintingLinesInCart();
    applyRetailNoMoqRules();

    if (typeof window.subscribe === 'function' && window.theme?.PUB_SUB_EVENTS?.variantChange) {
      window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        const productInfo = getProductInfo();
          if (!productInfo || data?.sectionId !== productInfo.dataset.section) return;
          applyRetailNoMoqRules(data.variant);
          scheduleEasifySync(productInfo);
        });
    }

    const productInfo = getProductInfo();
    if (!productInfo) return;

    observeEasifyLoad(productInfo);

    if (!productInfo.hasAttribute('data-retail-no-moq-easify-bound')) {
      productInfo.dataset.retailNoMoqEasifyBound = 'true';
      productInfo.addEventListener('change', (event) => {
        if (isResettingUnavailableLabels) return;

        const isEasifyOption = Boolean(event.target.closest('.tpo_option-container'));
        const isQuantityInput = event.target.matches('[data-quantity-input]');

        if (!isEasifyOption && !isQuantityInput) return;
        if (isLabelAutomationEnabled() && isEasifyOption) {
          const easifyBlock = findEasifyBlock(productInfo);
          const labelApplicationOption = getLabelApplicationInputs(easifyBlock).option;

          if (
            !isSyncingLabelApplication &&
            event.target.closest('.tpo_option-container') === labelApplicationOption
          ) {
            labelApplicationOption.dataset.retailNoMoqValidationReady = 'true';
            labelApplicationOption.classList.remove(DEFERRED_VALIDATION_CLASS);
          }

          enforceExclusiveLabelPrintingChoice(
            event.target,
            easifyBlock
          );
          enforceExclusiveLabelApplicationChoice(
            event.target,
            easifyBlock
          );
        }
        scheduleEasifySync(productInfo);
      });

      productInfo.addEventListener('click', (event) => {
        if (!event.target.closest('[data-add-to-cart]')) return;

        const selectedQuantity = getSelectedProductQuantity(
          productInfo,
          getSelectedVariant(productInfo)
        );
        if (selectedQuantity !== null) activeRetailPackQuantity = selectedQuantity;
        oneTimeLabelPrintingAccepted = false;
        oneTimeLabelPrintingWindowEndsAt = Date.now() + 10000;

        const labelApplicationOption = getLabelApplicationInputs(
          findEasifyBlock(productInfo)
        ).option;

        if (!labelApplicationOption) return;
        labelApplicationOption.dataset.retailNoMoqValidationReady = 'true';
        labelApplicationOption.classList.remove(DEFERRED_VALIDATION_CLASS);
      }, true);
    }

    scheduleEasifySync(productInfo);

  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialise, { once: true });
  } else {
    initialise();
  }
})();
